import React, { useCallback, useEffect, useState } from 'react';
import { api, apiPdfUrl } from '@/lib/api';
import { useI18n, formatCurrency, formatDate } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, FileText, Download, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Invoices() {
  const { t, lang } = useI18n();
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => { const r = await api.get('/invoices'); setInvoices(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold">{t('nav_invoices')}</h1>
        <Button onClick={() => setOpen(true)} data-testid="invoices-new-button"><Plus className="h-4 w-4 mr-1" /> {t('new_invoice')}</Button>
      </div>
      {invoices.length === 0 ? (
        <Card><CardContent className="text-center py-12">
          <FileText className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{lang === 'es' ? 'Las facturas se generan al cerrar pedidos en POS.' : 'As faturas são geradas ao fechar pedidos no POS.'}</p>
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-0"><div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted))]"><tr>
              <th className="text-left px-3 py-2 text-xs uppercase">{t('invoice_number')}</th>
              <th className="text-left px-3 py-2 text-xs uppercase">{t('date')}</th>
              <th className="text-left px-3 py-2 text-xs uppercase">{t('invoice_type')}</th>
              <th className="text-left px-3 py-2 text-xs uppercase">{t('customer_name')}</th>
              <th className="text-right px-3 py-2 text-xs uppercase">{t('base')}</th>
              <th className="text-right px-3 py-2 text-xs uppercase">{t('iva')}</th>
              <th className="text-right px-3 py-2 text-xs uppercase">{t('total')}</th>
              <th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>{invoices.map(inv => (
              <tr key={inv.id} className="border-t hover:bg-[hsl(var(--muted))]/40" data-testid={`invoice-row-${inv.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{inv.numero}</td>
                <td className="px-3 py-2 text-xs">{formatDate(inv.fecha, lang)}</td>
                <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{t(`invoice_type_${inv.tipo === 'factura_simplificada' ? 'simplificada' : inv.tipo === 'factura_completa' ? 'completa' : 'ticket'}`)}</Badge></td>
                <td className="px-3 py-2 text-xs">{inv.cliente_nombre || '-'}</td>
                <td className="px-3 py-2 text-right tabular-nums font-mono">{formatCurrency(inv.base, lang)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-mono">{formatCurrency(inv.iva_total, lang)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-mono font-semibold">{formatCurrency(inv.total, lang)}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => window.open(apiPdfUrl(`/invoices/${inv.id}/pdf`), '_blank')} data-testid={`invoice-pdf-${inv.id}-button`}>
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div></CardContent></Card>
      )}
      <ManualInvoiceDialog open={open} onOpenChange={setOpen} t={t} lang={lang} onSaved={load} />
    </div>
  );
}

function ManualInvoiceDialog({ open, onOpenChange, t, lang, onSaved }) {
  const [form, setForm] = useState({
    tipo: 'factura_completa',
    cliente_nombre: '', cliente_nif: '', cliente_direccion: '',
    pago_metodo: 'transferencia',
    lineas: [{ descripcion: '', cantidad: 1, precio_unitario: 0, iva_pct: 10 }],
    notas: '',
  });
  useEffect(() => { if (open) setForm({ tipo: 'factura_completa', cliente_nombre: '', cliente_nif: '', cliente_direccion: '', pago_metodo: 'transferencia', lineas: [{ descripcion: '', cantidad: 1, precio_unitario: 0, iva_pct: 10 }], notas: '' }); }, [open]);
  const save = async () => {
    const r = await api.post('/invoices', form);
    toast.success(`Factura ${r.data.numero}`);
    window.open(apiPdfUrl(`/invoices/${r.data.id}/pdf`), '_blank');
    onOpenChange(false); onSaved();
  };
  const addLine = () => setForm({ ...form, lineas: [...form.lineas, { descripcion: '', cantidad: 1, precio_unitario: 0, iva_pct: 10 }] });
  const total = form.lineas.reduce((acc, ln) => acc + (ln.cantidad || 0) * (ln.precio_unitario || 0), 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manual-invoice-dialog">
        <DialogHeader><DialogTitle>{t('new_invoice')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('invoice_type')}</Label>
              <Select value={form.tipo} onValueChange={v => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ticket">{t('invoice_type_ticket')}</SelectItem>
                  <SelectItem value="factura_simplificada">{t('invoice_type_simplificada')}</SelectItem>
                  <SelectItem value="factura_completa">{t('invoice_type_completa')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t('payment_method')}</Label>
              <Select value={form.pago_metodo} onValueChange={v => setForm({ ...form, pago_metodo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="efectivo">{t('cash')}</SelectItem><SelectItem value="tarjeta">{t('card')}</SelectItem><SelectItem value="transferencia">{t('transfer')}</SelectItem><SelectItem value="otro">{t('other')}</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>{t('customer_name')}</Label><Input value={form.cliente_nombre} onChange={e => setForm({ ...form, cliente_nombre: e.target.value })} /></div>
            <div><Label>{t('customer_nif')}</Label><Input value={form.cliente_nif} onChange={e => setForm({ ...form, cliente_nif: e.target.value })} /></div>
          </div>
          <div><Label>{t('customer_address')}</Label><Input value={form.cliente_direccion} onChange={e => setForm({ ...form, cliente_direccion: e.target.value })} /></div>
          <div>
            <div className="flex items-center justify-between mb-2"><Label>Líneas</Label><Button size="sm" variant="outline" onClick={addLine}><Plus className="h-3 w-3 mr-1" /></Button></div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--muted))]"><tr>
                  <th className="text-left px-2 py-1.5 text-xs">Desc.</th>
                  <th className="text-right px-2 py-1.5 text-xs">Cant.</th>
                  <th className="text-right px-2 py-1.5 text-xs">Precio</th>
                  <th className="text-right px-2 py-1.5 text-xs">IVA</th>
                  <th></th>
                </tr></thead>
                <tbody>{form.lineas.map((ln, idx) => (
                  <tr key={`inv-line-${idx}`} className="border-t">
                    <td className="px-1 py-1"><Input className="h-8 text-xs" value={ln.descripcion} onChange={e => { const arr = [...form.lineas]; arr[idx].descripcion = e.target.value; setForm({ ...form, lineas: arr }); }} /></td>
                    <td className="px-1 py-1"><Input type="number" step="0.01" className="h-8 text-xs text-right w-16" value={ln.cantidad} onChange={e => { const arr = [...form.lineas]; arr[idx].cantidad = Number(e.target.value); setForm({ ...form, lineas: arr }); }} /></td>
                    <td className="px-1 py-1"><Input type="number" step="0.01" className="h-8 text-xs text-right w-20" value={ln.precio_unitario} onChange={e => { const arr = [...form.lineas]; arr[idx].precio_unitario = Number(e.target.value); setForm({ ...form, lineas: arr }); }} /></td>
                    <td className="px-1 py-1"><Select value={String(ln.iva_pct)} onValueChange={v => { const arr = [...form.lineas]; arr[idx].iva_pct = Number(v); setForm({ ...form, lineas: arr }); }}><SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="4">4%</SelectItem><SelectItem value="10">10%</SelectItem><SelectItem value="21">21%</SelectItem></SelectContent></Select></td>
                    <td className="px-1"><button onClick={() => setForm({ ...form, lineas: form.lineas.filter((_, i) => i !== idx) })}><X className="h-3.5 w-3.5" /></button></td>
                  </tr>
                ))}</tbody>
                <tfoot className="bg-[hsl(var(--muted))]"><tr><td colSpan={2} className="px-2 py-1.5 text-xs text-right font-semibold">{t('total')} (con IVA)</td><td colSpan={3} className="px-2 py-1.5 text-right font-mono font-bold">{formatCurrency(total, lang)}</td></tr></tfoot>
              </table>
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!form.lineas.some(l => l.descripcion && l.precio_unitario)} data-testid="manual-invoice-save-button">{t('save')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
